import "./App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { LoadScript } from "@react-google-maps/api";
import { GoogleOAuthProvider } from "@react-oauth/google";

import LandingPage from "./Componets/landingpage/landingpage";
import Signup from "./Componets/Signup/Signup";
import SignIn from "./Componets/Login/Login";
import Category from "./Componets/Category/Category";
import ProductDetail from "./Componets/ProductDetail/ProductDetail";
import ProfilePage from "./Componets/Profile/Profile";
import WishlistPage from "./Componets/Profile/WishlistPage";
import Earnings from "./Componets/Profile/Earning";
import Setting from "./Componets/Profile/Setting";
import AboutUs from "./Componets/AboutUs/Aboutus";
import Contact from "./Componets/ContactUs/Contactus";
import DeleteAccount from "./Componets/DeleteAccount/DeleteAccount";
import PrivacyPolicy from "./Componets/PrivacyPolicy/PrivacyPolicy";
import BookingRequest from "./Componets/Profile/BookingRequest";
import AllProduct from "./Componets/AllProduct/AllProduct";
import MyOrders from "./Componets/Profile/MyOrder";
import BookingDetails from "./Componets/Profile/BookingDetails";
import OrderDetails from "./Componets/Profile/OrderDetail";
import OwnerProfile from "./Componets/OwnerProfile/OwnerProfile";
import ReturnRefundPolicy from "./Componets/RefundAndReturn/RefundAndReturn";
import TermsConditions from "./Componets/TermsAndCondition/TermsAndCondition";
import Terms from "./Componets/TermsAndCondition/Terms";
import ExploreAll from "./Componets/ExploreAll/ExploreAll";

import PageTracking from "./Componets/Analytics/PageTracking";

import {
  captureMarketingAttribution,
} from "./utils/marketingAttribution";

import config from "./Config";

/**
 * Scroll to the top whenever the route changes.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return null;
};

/**
 * Capture marketing attribution whenever the URL changes.
 *
 * This is important for SPA navigation and campaign landing URLs.
 */
const MarketingAttributionTracker = () => {
  const location = useLocation();

  useEffect(() => {
    captureMarketingAttribution();
  }, [location.pathname, location.search]);

  return null;
};

/**
 * Old owner profile URL:
 *
 * /ownerProfile?ownerId=123
 *
 * redirects to:
 *
 * /ownerprofile/123
 */
function OwnerProfileRedirect() {
  const [searchParams] = useSearchParams();

  const ownerId = searchParams.get("ownerId");

  if (!ownerId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate
      to={`/ownerprofile/${encodeURIComponent(ownerId)}`}
      replace
    />
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={config.googleClientId}>
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["places"]}
      >
        <div className="App">
          <BrowserRouter>
            {/* Scroll handling */}
            <ScrollToTop />

            {/* Marketing attribution */}
            <MarketingAttributionTracker />

            {/* Analytics / page tracking */}
            <PageTracking />

            <Routes>
              {/* HOME */}
              <Route
                path="/"
                element={<LandingPage />}
              />

              {/* AUTH */}
              <Route
                path="/signup"
                element={<Signup />}
              />

              <Route
                path="/Signin"
                element={<SignIn />}
              />

              {/* EXPLORE */}
              <Route
                path="/explore/collection/:collectionId"
                element={<ExploreAll />}
              />

              <Route
                path="/explore/:collectionId"
                element={<ExploreAll />}
              />

              {/* CATEGORY */}
              <Route
                path="/category/:mainCat/:subCat/:subId"
                element={<Category />}
              />

              {/* PRODUCT DETAIL */}
              <Route
                path="/productdetail/:mainCat/:subCat/:productId"
                element={<ProductDetail />}
              />

              <Route
                path="/productdetail/:productId"
                element={<ProductDetail />}
              />

              {/* Legacy URL */}
              <Route
                path="/ProductDetail/:productId"
                element={<ProductDetail />}
              />

              {/* PROFILE */}
              <Route
                path="/profile"
                element={<ProfilePage />}
              />

              <Route
                path="/booking-request"
                element={<BookingRequest />}
              />

              <Route
                path="/wishlist"
                element={<WishlistPage />}
              />

              <Route
                path="/earnings"
                element={<Earnings />}
              />

              <Route
                path="/settings"
                element={<Setting />}
              />

              {/* INFORMATION */}
              <Route
                path="/about-us"
                element={<AboutUs />}
              />

              <Route
                path="/contact-us"
                element={<Contact />}
              />

              <Route
                path="/account-delete-request"
                element={<DeleteAccount />}
              />

              {/* PRIVACY */}
              <Route
                path="/privacy"
                element={<PrivacyPolicy />}
              />

              <Route
                path="/privacy/:id"
                element={<PrivacyPolicy />}
              />

              {/* BOOKING */}
              <Route
                path="/Booking"
                element={<BookingRequest />}
              />

              <Route
                path="/BookingDetails/:bookingId"
                element={<BookingDetails />}
              />

              {/* PRODUCTS */}
              <Route
                path="/AllProduct"
                element={<AllProduct />}
              />

              {/* ORDERS */}
              <Route
                path="/orders"
                element={<MyOrders />}
              />

              <Route
                path="/orderDetail/:orderId"
                element={<OrderDetails />}
              />

              {/* OWNER PROFILE */}
              <Route
                path="/ownerProfile"
                element={<OwnerProfileRedirect />}
              />

              <Route
                path="/ownerprofile/:ownerid"
                element={<OwnerProfile />}
              />

              {/* RETURN / REFUND */}
              <Route
                path="/ReturnRefundPolicy"
                element={<ReturnRefundPolicy />}
              />

              <Route
                path="/ReturnRefundPolicy/:id"
                element={<ReturnRefundPolicy />}
              />

              {/* TERMS */}
              <Route
                path="/TermsConditions"
                element={<TermsConditions />}
              />

              <Route
                path="/TermsConditions/:id"
                element={<TermsConditions />}
              />

              <Route
                path="/terms"
                element={<Terms />}
              />

              {/* FALLBACK */}
              <Route
                path="*"
                element={<Navigate to="/" replace />}
              />
            </Routes>
          </BrowserRouter>
        </div>
      </LoadScript>
    </GoogleOAuthProvider>
  );
}

export default App;